import {useEffect, useRef, useState} from "react";

/**
 * Reads a chunked text/plain streaming response into state as it arrives.
 *
 * Guards against React StrictMode's dev-only double-invoke of effects: that
 * double-invoke reruns this effect for the *same* id before the fetch it
 * started has finished. Without a guard, that second run would fire a
 * second, duplicate request to `fetchStream`. Instead, once a request has
 * started for a given id, later effect runs for that same id are no-ops —
 * they let the original request's reader keep streaming into state.
 *
 * A real change to `id` (navigating to a different problem/group, not
 * StrictMode's synthetic remount) still starts a fresh request and stale
 * updates from the abandoned one are dropped via the `id` comparison inside
 * `load`.
 */
export function useStreamedText(id: string | undefined, fetchStream: (id: string) => Promise<Response>) {
    const [loading, setLoading] = useState(true);
    const [streaming, setStreaming] = useState(true);
    const [text, setText] = useState("");
    const [error, setError] = useState<string | null>(null);

    const startedForRef = useRef<string | null>(null);
    const currentIdRef = useRef<string | undefined>(undefined);

    useEffect(() => {
        if (!id) return;

        currentIdRef.current = id;

        if (startedForRef.current === id) return;
        startedForRef.current = id;

        setLoading(true);
        setStreaming(true);
        setText("");
        setError(null);

        const load = async () => {
            try {
                const res = await fetchStream(id);

                if (currentIdRef.current !== id) return;

                if (!res?.ok) {
                    setLoading(false);
                    setStreaming(false);
                    return;
                }

                const reader = res.body!.getReader();
                const decoder = new TextDecoder();

                let fullText = "";
                let first = true;

                while (true) {
                    const {done, value} = await reader.read();

                    if (done) break;
                    if (currentIdRef.current !== id) {
                        reader.cancel();
                        return;
                    }

                    fullText += decoder.decode(value, {stream: true});

                    if (first) {
                        setLoading(false);
                        first = false;
                    }
                    setText(fullText);
                }

                if (currentIdRef.current === id) {
                    setStreaming(false);
                }
            } catch (err) {
                // fetchStream (getNodeText) throws on a non-ok response (e.g.
                // a 429 from the daily quota, or a 403) — without this catch,
                // that throw was an unhandled rejection that left `loading`
                // stuck true forever with nothing on screen ever resolving,
                // and no way for the caller to know something went wrong.
                if (currentIdRef.current !== id) return;
                setError(err instanceof Error ? err.message : "Failed to load problem");
                setLoading(false);
                setStreaming(false);
            }
        };

        load();
    }, [id, fetchStream]);

    return {loading, streaming, text, error};
}
